import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaLongArrowAltRight } from "react-icons/fa";
import { BsGraphUpArrow } from "react-icons/bs";
import vectorbg from "../asset/blog/Vectorbg.png";
import SummaryApi from "../common";
import NoImage from "../asset/noimage.png";
import { useNavigate } from "react-router-dom";

const LatestBlog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchActive = async () => {
    try {
      const response = await fetch(SummaryApi.getActiveBlog.url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();
      console.log("Fetched blogs:", result);

      if (response.ok && result.status && Array.isArray(result.data)) {
        setBlogs(result.data);
      } else {
        console.error("Unexpected API response:", result);
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActive();
  }, []);

  const getFullMediaUrl = (relativePath) => {
    if (!relativePath) return null;
    const normalized = relativePath.replace(/\\/g, "/");
    const base = SummaryApi.Images.url.replace("/api", "");
    return `${base}/${normalized}`;
  };

  const getCategory = (category) => {
    return category?.trim() ? category : "No Category";
  };

  const MAX_DISPLAY = 7;

  return (
    <section className="py-16 px-4 md:px-8 lg:px-16 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-extrabold text-blue-900 dark:text-white">
            Latest Blog Posts
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Stay updated with the latest industry trends, interview questions,
            and tech tutorials
          </p>
        </div>

        {loading ? (
          <p className="text-center text-gray-500 dark:text-gray-400">Loading...</p>
        ) : blogs.length === 0 ? (
          <p className="text-center text-gray-500 dark:text-gray-400">No active blogs found.</p>
        ) : (
          <>
            {/* Top Section */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Featured Blog (blogs[0]) */}
              <div className="col-span-1 lg:col-span-3 bg-blue-900 dark:bg-gray-800 rounded-2xl p-6 text-white relative flex flex-col">
                <img
                  src={getFullMediaUrl(blogs[0]?.blog_image) || NoImage}
                  alt={blogs[0]?.title}
                  className="rounded-xl w-full h-64 object-cover"
                />
                <div className="mt-4">
                  <p className="text-sm mb-2 flex items-center gap-2 text-white">
                    <span className="flex font-medium">
                      <span className="mt-1 pr-1">
                        <FaCalendarAlt />
                      </span>{" "}
                      {new Date(blogs[0].blog_date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                    <span className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300 text-xs px-2 py-1 rounded">
                      {getCategory(blogs[0].category)}
                    </span>
                  </p>
                  <h3 className="text-white font-semibold text-xl mb-4 leading-snug">
                    {blogs[0].title}
                  </h3>
                  <button
                    onClick={() => navigate(`/blog-details/${btoa(blogs[0].blog_id)}`)}
                    className="text-blue-200 dark:text-blue-300 text-sm font-medium hover:underline"
                  >
                    Read More
                  </button>
                </div>
              </div>

              {/* Middle Two Blogs */}
              <div className="col-span-1 flex flex-col gap-6">
                {blogs.slice(1, 3).map((blog) => (
                  <div
                    key={blog.blog_id}
                    className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
                  >
                    <p className="text-sm mb-2 flex items-center gap-2">
                      <span className="flex font-medium text-gray-700 dark:text-gray-300">
                        <span className="mt-1 pr-1">
                          <FaCalendarAlt />
                        </span>{" "}
                        {new Date(blog.blog_date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                      <span className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300 text-xs px-2 py-1 rounded">
                        {getCategory(blog.category)}
                      </span>
                    </p>
                    <h3 className="text-blue-900 dark:text-white font-semibold text-lg mb-4 leading-snug">
                      {blog.title}
                    </h3>
                    <button
                      onClick={() => navigate(`/blog-details/${btoa(blog.blog_id)}`)}
                      className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                    >
                      Read More
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom Blog Grid */}
            {blogs.length > 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                {blogs.slice(3, 7).map((blog) => (
                  <div
                    key={blog.blog_id}
                    className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
                  >
                    <p className="flex justify-between text-sm mb-2 gap-2">
                      <span className="flex font-medium text-gray-700 dark:text-gray-300">
                        <span className="mt-1 pr-1">
                          <FaCalendarAlt />
                        </span>{" "}
                        {new Date(blog.blog_date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                      {blog.trending && (
                        <span className="text-bold text-orange-600 dark:text-orange-400">
                          <BsGraphUpArrow />
                        </span>
                      )}
                    </p>
                    <span className="bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300 text-[12px] px-2 py-1 rounded">
                      {getCategory(blog.category)}
                    </span>
                    <h3 className="text-blue-900 dark:text-white font-semibold text-lg mb-4 mt-2">
                      {blog.title}
                    </h3>
                    <button
                      onClick={() => navigate(`/blog-details/${btoa(blog.blog_id)}`)}
                      className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                    >
                      Read More
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* View More Button */}
            {blogs.length > MAX_DISPLAY && (
              <div className="text-center mt-8">
                <button
                  onClick={() => navigate("/all-blogs")}
                  className="px-6 py-3 bg-[#103b6b] dark:bg-blue-700 text-white rounded-md hover:bg-blue-800 dark:hover:bg-blue-600 transition duration-300 flex items-center justify-center gap-2 mx-auto"
                >
                  View More
                  <span className="ml-1">
                    <FaLongArrowAltRight />
                  </span>
                </button>
              </div>
            )}
          </>
        )}

        {/* Bottom Banner */}
        <div className="max-w-7xl mx-auto my-[3%]">
          <div
            className="relative bg-cover bg-center rounded-lg py-12 px-6 text-center text-white"
            style={{
              backgroundImage: `url(${vectorbg})`,
            }}
          >
            <div className="relative z-10">
              <p className="text-2xl md:text-3xl font-bold drop-shadow-md">
                Check out our blog for free insights and updates on your field
              </p>
              <button
                className="mt-6 px-6 py-3 bg-[#103b6b] dark:bg-blue-700 text-white rounded-md hover:bg-blue-800 dark:hover:bg-blue-600 transition duration-300 flex items-center justify-center gap-2 mx-auto"
                onClick={() => navigate("/all-blogs")}
              >
                Show Me More
                <span className="ml-1">
                  <FaLongArrowAltRight />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LatestBlog;